import type { GridPoint } from '../../core/types';

/** Can a unit stand on / pass through tile (x, y)? Out-of-bounds must return false. */
export type Passable = (x: number, y: number) => boolean;

export interface PathResult {
  /** Tiles to walk through, EXCLUDING the start, ending at `reached`. Empty if already there. */
  readonly path: GridPoint[];
  /** The goal, or the reachable tile closest to it when the goal cannot be reached. */
  readonly reached: GridPoint;
  /** True when `reached` is the requested goal. */
  readonly complete: boolean;
}

const SQRT2 = Math.SQRT2;
const DIRS: readonly [number, number, number][] = [
  [1, 0, 1], [-1, 0, 1], [0, 1, 1], [0, -1, 1],
  [1, 1, SQRT2], [1, -1, SQRT2], [-1, 1, SQRT2], [-1, -1, SQRT2],
];

/**
 * Grid A* with 8-way moves (diagonals only when both side tiles are open, so
 * units never cut through building or rock corners). Octile heuristic.
 * Deterministic: ties broken by insertion order. Bounded by `maxNodes`; when
 * the goal is unreachable (or the budget runs out) it returns the path to the
 * explored tile closest to the goal.
 */
export function findPath(width: number, height: number, passable: Passable, start: GridPoint, goal: GridPoint, maxNodes = 20000): PathResult {
  if (start.x === goal.x && start.y === goal.y) return { path: [], reached: { ...goal }, complete: true };
  const n = width * height;
  const idx = (x: number, y: number) => y * width + x;
  const g = new Float64Array(n).fill(Infinity);
  const parent = new Int32Array(n).fill(-1);
  const closed = new Uint8Array(n);
  const heap = new MinHeap();
  const h = (x: number, y: number) => {
    const dx = Math.abs(x - goal.x);
    const dy = Math.abs(y - goal.y);
    return dx + dy + (SQRT2 - 2) * Math.min(dx, dy);
  };
  const s = idx(start.x, start.y);
  g[s] = 0;
  heap.push(s, h(start.x, start.y));
  let best = s;
  let bestH = h(start.x, start.y);
  let expanded = 0;
  const goalOpen = goal.x >= 0 && goal.y >= 0 && goal.x < width && goal.y < height && passable(goal.x, goal.y);
  while (heap.size > 0 && expanded < maxNodes) {
    const cur = heap.pop();
    if (closed[cur]) continue;
    closed[cur] = 1;
    expanded++;
    const cx = cur % width;
    const cy = (cur - cx) / width;
    const ch = h(cx, cy);
    if (ch < bestH || (ch === bestH && g[cur]! < g[best]!)) {
      best = cur;
      bestH = ch;
    }
    if (goalOpen && cx === goal.x && cy === goal.y) break;
    for (const [dx, dy, cost] of DIRS) {
      const nx = cx + dx;
      const ny = cy + dy;
      if (nx < 0 || ny < 0 || nx >= width || ny >= height || !passable(nx, ny)) continue;
      if (dx !== 0 && dy !== 0 && (!passable(cx + dx, cy) || !passable(cx, cy + dy))) continue;
      const ni = idx(nx, ny);
      if (closed[ni]) continue;
      const ng = g[cur]! + cost;
      if (ng < g[ni]!) {
        g[ni] = ng;
        parent[ni] = cur;
        heap.push(ni, ng + h(nx, ny));
      }
    }
  }
  const end = goalOpen && closed[idx(goal.x, goal.y)] ? idx(goal.x, goal.y) : best;
  const path: GridPoint[] = [];
  for (let c = end; c !== s && c !== -1; c = parent[c]!) path.push({ x: c % width, y: Math.floor(c / width) });
  path.reverse();
  const reached = { x: end % width, y: Math.floor(end / width) };
  return { path, reached, complete: reached.x === goal.x && reached.y === goal.y };
}

/** Binary min-heap of (node, priority); FIFO among equal priorities for determinism. */
class MinHeap {
  private nodes: number[] = [];
  private pri: number[] = [];
  private seq: number[] = [];
  private counter = 0;

  get size(): number {
    return this.nodes.length;
  }

  push(node: number, p: number): void {
    this.nodes.push(node);
    this.pri.push(p);
    this.seq.push(this.counter++);
    this.up(this.nodes.length - 1);
  }

  pop(): number {
    const top = this.nodes[0]!;
    const last = this.nodes.length - 1;
    this.swap(0, last);
    this.nodes.pop();
    this.pri.pop();
    this.seq.pop();
    this.down(0);
    return top;
  }

  private less(a: number, b: number): boolean {
    return this.pri[a]! < this.pri[b]! || (this.pri[a] === this.pri[b] && this.seq[a]! < this.seq[b]!);
  }

  private swap(a: number, b: number): void {
    [this.nodes[a], this.nodes[b]] = [this.nodes[b]!, this.nodes[a]!];
    [this.pri[a], this.pri[b]] = [this.pri[b]!, this.pri[a]!];
    [this.seq[a], this.seq[b]] = [this.seq[b]!, this.seq[a]!];
  }

  private up(i: number): void {
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (!this.less(i, p)) break;
      this.swap(i, p);
      i = p;
    }
  }

  private down(i: number): void {
    const n = this.nodes.length;
    for (;;) {
      const l = i * 2 + 1;
      const r = l + 1;
      let m = i;
      if (l < n && this.less(l, m)) m = l;
      if (r < n && this.less(r, m)) m = r;
      if (m === i) break;
      this.swap(i, m);
      i = m;
    }
  }
}

/**
 * Up to `count` distinct passable tiles nearest to `center` (BFS rings), for
 * spreading a group's destinations. Deterministic order.
 */
export function nearestOpenTiles(width: number, height: number, passable: Passable, center: GridPoint, count: number, exclude: ReadonlySet<number> = new Set(), maxVisit = 4000): GridPoint[] {
  const out: GridPoint[] = [];
  const seen = new Uint8Array(width * height);
  const q: GridPoint[] = [];
  const inB = (x: number, y: number) => x >= 0 && y >= 0 && x < width && y < height;
  if (!inB(center.x, center.y)) return out;
  q.push(center);
  seen[center.y * width + center.x] = 1;
  let head = 0;
  while (head < q.length && out.length < count && head < maxVisit) {
    const t = q[head++]!;
    const i = t.y * width + t.x;
    if (passable(t.x, t.y) && !exclude.has(i)) out.push(t);
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]] as const) {
      const nx = t.x + dx;
      const ny = t.y + dy;
      if (!inB(nx, ny) || seen[ny * width + nx]) continue;
      seen[ny * width + nx] = 1;
      q.push({ x: nx, y: ny });
    }
  }
  return out;
}
